/**
 * useWindParticles Composable
 * Orchestrates the wind particle overlay lifecycle: data fetching, overlay management, and settings.
 */

import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import type { MapPreferences } from "@/stores/settings.store";
import type { ViewportBounds } from "@/services/windData.service";
import { useWindStore } from "@/stores/wind.store";
import {
  createWindParticleOverlay,
  type IWindParticleOverlay,
} from "@/utils/WindParticleOverlay";

export function useWindParticles(
  preferences: () => Pick<MapPreferences, "showWindLayer" | "windParticleOpacity">,
) {
  const showWindLayer = computed(() => preferences().showWindLayer);
  const windParticleOpacity = computed(() => preferences().windParticleOpacity);

  const isWindLayerActive = ref(false);
  const wind = useWindStore();
  const { loading: isLoading, error: windError } = storeToRefs(wind);
  let generation = 0;

  let overlay: IWindParticleOverlay | null = null;
  let map: google.maps.Map | null = null;
  let fetchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let suspended = false;

  /**
   * Initialize the wind overlay on a map instance.
   * Must be called after Google Maps is loaded.
   */
  function initOverlay(mapInstance: google.maps.Map): void {
    map = mapInstance;
    overlay = createWindParticleOverlay();

    // Apply current settings
    overlay.setOpacity(windParticleOpacity.value);

    // If wind layer was previously enabled (persisted setting), activate it
    if (showWindLayer.value) {
      activateOverlay();
    }
  }

  function activateOverlay(): void {
    if (!overlay || !map || suspended) return;
    overlay.setMap(map);
    overlay.startAnimation();
    isWindLayerActive.value = true;

    // Fetch wind data for current viewport
    triggerFetch();
  }

  function deactivateOverlay(): void {
    generation++;
    if (fetchDebounceTimer) clearTimeout(fetchDebounceTimer);
    if (!overlay) return;
    overlay.stopAnimation();
    overlay.setMap(null);
    isWindLayerActive.value = false;
  }

  /**
   * Called when the map becomes idle (after pan/zoom).
   * Debounces and fetches wind grid if the layer is active.
   */
  function onMapIdle(): void {
    if (!showWindLayer.value || suspended) return;
    triggerFetch();
  }

  function triggerFetch(): void {
    if (suspended) return;
    if (fetchDebounceTimer) clearTimeout(fetchDebounceTimer);
    fetchDebounceTimer = setTimeout(async () => {
      await fetchAndApplyGrid();
    }, 800);
  }

  async function fetchAndApplyGrid(): Promise<void> {
    if (!map || !overlay) return;

    const bounds = map.getBounds();
    const zoom = map.getZoom();
    if (!bounds || zoom === undefined) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const viewportBounds: ViewportBounds = {
      north: ne.lat(),
      south: sw.lat(),
      east: ne.lng(),
      west: sw.lng(),
    };

    const run = ++generation;
    const grid = await wind.load(viewportBounds, zoom);
    if (
      run === generation &&
      grid &&
      overlay &&
      showWindLayer.value &&
      !suspended
    )
      overlay.setWindGrid(grid);
  }

  /** Stop animation and pending work while a cached map is hidden. */
  function suspend(): void {
    if (suspended) return;
    suspended = true;
    generation++;
    if (fetchDebounceTimer) clearTimeout(fetchDebounceTimer);
    fetchDebounceTimer = null;
    overlay?.stopAnimation();
  }

  /** Restart the retained overlay and load its viewport through the store's cache.
   * Suspension may have cancelled the initial debounce or invalidated an in-flight grid.
   */
  function resume(): void {
    if (!suspended) return;
    suspended = false;
    if (!showWindLayer.value) return;
    if (isWindLayerActive.value) {
      overlay?.startAnimation();
      triggerFetch();
    } else {
      activateOverlay();
    }
  }

  /**
   * Clean up overlay and cancel pending fetches.
   */
  function cleanup(): void {
    generation++;
    suspended = false;
    if (fetchDebounceTimer) {
      clearTimeout(fetchDebounceTimer);
      fetchDebounceTimer = null;
    }
    if (overlay) {
      overlay.stopAnimation();
      overlay.setMap(null);
      overlay = null;
    }
    map = null;
    isWindLayerActive.value = false;
  }

  // Watch settings changes
  watch(showWindLayer, (enabled) => {
    if (enabled) {
      activateOverlay();
    } else {
      deactivateOverlay();
    }
  });

  watch(windParticleOpacity, (opacity) => {
    overlay?.setOpacity(opacity);
  });

  return {
    isWindLayerActive,
    isLoading,
    windError,
    initOverlay,
    onMapIdle,
    suspend,
    resume,
    cleanup,
  };
}
